import { requestLogDelivery } from '@apis/renewal/log';
import getGa4UserProperty from '@tags/ga4/gaPageViewProperty';
import { gaVirtualPageView } from '@utils/ga4';
import utils from '@utils/index';
import {
  FilterItem,
  Sort,
  VodInfo,
} from '@wconcept/wconcept-common/models/renewal/common';
import {
  DiscoveryItem,
  DiscoveryType,
  ShopLive,
} from '@wconcept/wconcept-common/models/renewal/discovery';
import { ShortFormAdInfo } from '@wconcept/wconcept-common/models/renewal/moloco';
import { MyHeartContentType } from '@wconcept/wconcept-common/models/renewal/myHeart';
import { Product } from '@wconcept/wconcept-common/models/renewal/product';
import { LandingType } from '@wconcept/wconcept-common/models/renewal/promotion';
import { getByteplusPlatform } from '@wconcept/wconcept-common/utils/deviceUtil';
import { useCallback } from 'react';
import { sendGAClickEvent, sendGAVirEcommerce } from '../tags/ga4/gaTags';
import useByteplusLogger from './useByteplusLogger';
import useGlobalState from './useGlobalState';

interface Item {
  id: string;
  name: string;
  price: number;
  brand: string;
  category: string;
  category2: string;
  category3: string;
  category4: string;
  category5: string;
  list_id: string;
  list_name: string;
  index: number;
  discount?: number;
}

interface ContentViewInfo {
  contentsCat: string;
  contentsDetail: string;
  style: string;
  tpo: string;
  trend: string;
  approvalDate: string;
  issueCD: string;
  issueNM: string;
}

interface Content {
  id: string;
  title: string;
  tabName: string;
}

interface CommonGa4EventParams {
  area?: string;
  text?: string;
  listNo?: number | string;
  tabNm?: string;
  page?: string;
  issueCd?: string;
  issueNm?: string;
  variable?: string;
  exclusiveYn?: string | boolean;
  referralID?: string;
  affiliation?: string;
  referralCat?: string;
  referralNM?: string;
  clickTraceId?: string;
  sortType?: string;
  myheartType?: string;
  pageNo?: number;
}

function useGa4Event() {
  const { pageInfo, appInfo, isMobile, domainGender, assistData } =
    useGlobalState();
  const { writeUserEvents } = useByteplusLogger();

  const getShortFormTabName = (tab: string) => {
    switch (tab) {
      case 'recommend':
        return '추천';
      case 'ranking':
        return '랭킹';
      case 'following':
        return '팔로잉';
      case 'profile':
        return '프로필';
      default:
        return '추천';
    }
  };

  const getEpDevice = useCallback(() => {
    if (appInfo?.isNativeAppBoth) {
      return 'Mobile App';
    }
    if (!isMobile) {
      return 'PC Web';
    }
    return 'Mobile Web';
  }, [appInfo?.isNativeAppBoth, isMobile]);

  const getContentReferralId = (content: DiscoveryItem) => {
    if (content?.contentType === 'STYLE_CLIP') {
      return content.styleClip?.id?.toString();
    }
    if (content?.contentType === 'BRAND_STORY') {
      return content.brandStory?.eventId?.toString();
    }
    if (content?.contentType === 'W_SERIES') {
      return content.wseries?.eventId?.toString();
    }
    if (content?.contentType === 'SHOP_LIVE') {
      return content.shopLive?.id?.toString();
    }
    if (content?.contentType === 'LOOKBOOK') {
      return content.lookBook?.id?.toString();
    }
    return undefined;
  };

  const getAdContentReferralId = ({
    content,
    ad,
  }: {
    content: DiscoveryItem;
    ad?: ShortFormAdInfo;
  }) => {
    // 기본 ID 확인
    const baseId = getContentReferralId(content);
    if (baseId) return baseId;

    // 광고 타입 처리
    if (content?.contentType === 'AD_BANNER') {
      return ad?.campaignId;
    }

    return undefined;
  };

  const getContentTypeText = (
    contentType?: DiscoveryType | MyHeartContentType | string
  ) => {
    if (contentType === 'STYLE_CLIP') {
      return '스타일링';
    }
    if (contentType === 'BRAND_STORY') {
      return '브랜드스토리';
    }
    if (contentType === 'LOOKBOOK') {
      return '룩북';
    }
    if (contentType === 'SHOP_LIVE') {
      return '라이브';
    }
    if (contentType === 'W_SERIES') {
      return 'W시리즈';
    }
    if (contentType === 'EVENT') {
      return '기획전';
    }
    if (contentType === 'STYLING') {
      return '스타일링';
    }
    if (contentType === 'AD_BANNER') {
      return '광고';
    }
    if (contentType === 'PROMOTION') {
      return '프로모션';
    }
    return contentType;
  };

  const getCornerAreaName = (processType?: string) => {
    // BP001 구좌 단독,지금 핫한브랜드, 뷰티, 라이프, 액티브 분기처리
    // if (processType === 'BP00101') return 'hotbrand';
    if (processType === 'BP00102') return 'exclusive';
    if (processType === 'BP00103') return 'beautycate';
    if (processType === 'BP00104') return 'lifecate';
    if (processType === 'BP00105') return 'activecate';
    return 'hotbrand';
  };

  const getContentFormType = ({
    vodInfo,
    shopLive,
    isVideo,
  }: {
    vodInfo?: VodInfo;
    shopLive?: ShopLive;
    isVideo?: boolean;
  }) => {
    // 앱 조건과 같음, id 필수, token 또는 playBackUrl 값이 있으면 영상, 라이브의 경우 campaignKey가 있으면 영상
    if (
      (vodInfo?.id && (vodInfo?.token || vodInfo?.playBackUrl)) ||
      shopLive?.campaignKey ||
      isVideo
    ) {
      return 'video';
    }
    return 'image';
  };

  const getContentName = (content: DiscoveryItem) => {
    if (content?.contentType === 'STYLE_CLIP') {
      return content.styleClip?.profileInfo?.profile?.nickName;
    }
    if (content?.contentType === 'BRAND_STORY') {
      return `${content.brandStory?.originalTitle1}${
        content.brandStory?.originalTitle2
          ? ` ${content.brandStory?.originalTitle2}`
          : ''
      }`;
    }
    if (content?.contentType === 'W_SERIES') {
      return `${content.wseries?.originalTitle1}${
        content.wseries?.originalTitle2
          ? ` ${content.wseries?.originalTitle2}`
          : ''
      }`;
    }
    if (content?.contentType === 'SHOP_LIVE') {
      return content.shopLive?.title;
    }
    if (content?.contentType === 'LOOKBOOK') {
      return content.lookBook?.lookBookTitle;
    }
    return '';
  };

  const getAdContentName = ({
    content,
    ad,
  }: {
    content: DiscoveryItem;
    ad?: ShortFormAdInfo;
  }) => {
    // 기본 Nm 확인
    const baseName = getContentName(content);
    if (baseName) return baseName;

    // 광고 타입 처리
    if (content?.contentType === 'AD_BANNER') {
      return ad?.brandNameKr;
    }
    return '';
  };

  const getCommonParams = useCallback(
    (page?: string) => ({
      ep_click_page: page || pageInfo?.page,
      ep_visit_device: getEpDevice(),
      transport: 'beacon',
    }),
    [pageInfo, getEpDevice]
  );

  const getItemCategory5 = (itemNameFront: string, itemNameBack: string) => {
    let itemCategoryName = '';
    if (itemNameFront && !itemNameBack) {
      itemCategoryName = `${itemNameFront}`;
    }
    if (!itemNameFront && itemNameBack) {
      itemCategoryName = `${itemNameBack}`;
    }
    if (itemNameFront && itemNameBack) {
      itemCategoryName = `${itemNameFront} | ${itemNameBack}`;
    }
    return itemCategoryName === '' ? undefined : itemCategoryName;
  };

  const getSortText = (type = '', list?: Sort[]) => {
    // 판매순, 신상품순, 할인율순, 낮은가격순, 높은가격순, 리뷰순, 추천순
    const result = list?.find(
      sort => sort.code.toLowerCase() === type.toLowerCase()
    );
    if (result) {
      return result.name;
    }
    return '';
  };

  const getExclusiveTag = (exclusiveYn?: boolean, product?: Product) => {
    if (exclusiveYn) {
      return product?.infoTags?.includes('단독') ? 'Y' : 'N';
    }
    return undefined;
  };

  // 배너클릭시 getIssueCd 함수
  const getIssueCd = (
    ga4ClickEventType: string,
    targetPageType: string,
    targetPageParameter: string
  ) => {
    let clickIssueCd = '';
    if (
      ga4ClickEventType?.toLowerCase() === 'issue' ||
      targetPageType?.toLowerCase() === 'issue' ||
      targetPageParameter?.startsWith('/Issue')
    ) {
      // eslint-disable-next-line no-return-assign
      return (clickIssueCd = targetPageParameter?.startsWith('/Issue')
        ? `${targetPageParameter?.replace('/Issue/', '')}`
        : `${targetPageParameter}`);
    }
    return clickIssueCd;
  };

  const getFilterValue = useCallback(
    (data: FilterItem[], value: string) => {
      const filterValue = data
        ?.filter(item => item.filterType === value)
        .flatMap(items => items.name);
      if (value === 'category') {
        const categoryValue = data
          ?.filter(item => item.filterType === value)
          .flatMap(items => items.name)
          .join('>');

        if (utils.isEmpty(categoryValue)) {
          return undefined;
        }
        if (appInfo?.isNativeAppBoth) {
          return categoryValue.toString();
        }
        return [categoryValue];
      }
      if (value === 'priceRange') {
        const priceValue = data?.filter(item => item.filterType === value)[0];
        const minPrice = priceValue
          ? priceValue?.value?.[0]?.toString()
          : undefined;
        const maxPrice = priceValue
          ? priceValue?.value?.[1]?.toString()
          : undefined;
        if (utils.isEmpty(priceValue)) {
          return undefined;
        }
        if (appInfo?.isNativeAppBoth) {
          return `${minPrice}~${maxPrice}`;
        }
        return `${minPrice}~${maxPrice}`;
      }
      if (value === 'benefit') {
        // 상품정보 탭은 상품 유형(productType)과 혜택(benefit)을 한 화면에서 고르므로
        // 두 그룹의 선택값을 ep_filter_benefit 하나로 합쳐 보낸다
        const benefitValue = data
          ?.filter(item =>
            ['discount', 'productType'].includes(item.filterType as string)
          )
          .map(items => items.name)
          .concat(filterValue);
        if (utils.isEmpty(benefitValue)) {
          return undefined;
        }
        if (appInfo?.isNativeAppBoth) {
          return benefitValue.toString();
        }
        return benefitValue;
      }

      if (value === 'brand') {
        const brandValue = data
          ?.filter(item =>
            ['brand', 'topBrand', 'newBrand'].includes(
              item.filterType as string
            )
          )
          .flatMap(item => item.name);

        if (utils.isEmpty(brandValue)) {
          return undefined;
        }
        if (appInfo?.isNativeAppBoth) {
          return brandValue.toString();
        }
        return brandValue;
      }

      if (utils.isEmpty(filterValue)) {
        return undefined;
      }
      if (appInfo?.isNativeAppBoth) {
        return filterValue.toString();
      }
      return filterValue;
    },
    [appInfo?.isNativeAppBoth]
  );

  const getFilterParams = useCallback(
    (selectedFilters?: FilterItem[]) => {
      if (!selectedFilters) return false;
      return {
        ep_filter_category: getFilterValue(selectedFilters, 'category'),
        ep_filter_color: getFilterValue(selectedFilters, 'color'),
        ep_filter_price: getFilterValue(selectedFilters, 'priceRange'),
        ep_filter_benefit: getFilterValue(selectedFilters, 'benefit'),
        ep_filter_brand: getFilterValue(selectedFilters, 'brand'),
        // styleClip
        ep_filter_style: getFilterValue(selectedFilters, 'style'),
        ep_filter_tpo: getFilterValue(selectedFilters, 'tpo'),
        ep_filter_season: getFilterValue(selectedFilters, 'season'),
      };
    },
    [getFilterValue]
  );

  const sendGa4SelectItem = useCallback(
    ({
      product,
      listNo,
      pageNo,
      ecommercePath,
      area,
      tabNm,
      sortType,
      periodType,
      affiliation,
      exclusiveYn,
      page,
      referralID,
      referralCat,
      referralNM,
      variable,
      playYn,
      pageIssueCd,
      clickTraceId,
      itemListName,
    }: CommonGa4EventParams & {
      product?: Product;
      sortType?: string;
      periodType?: string;
      affiliation?: string;
      ecommercePath?: string;
      pageNo?: number;
      exclusiveYn?: boolean;
      page?: string;
      referralID?: string;
      referralCat?: string;
      referralNM?: string;
      playYn?: string;
      pageIssueCd?: string;
      clickTraceId?: string;
      itemListName?: string;
    }) => {
      const commonParams = getCommonParams(page);
      const eventName = 'select_item';
      const data = {
        event_name: eventName,
        ...commonParams,
        ep_click_area: area,
        ep_click_listNo: (listNo || 1).toString(),
        ep_click_pageNo: pageNo?.toString() || undefined,
        ep_click_tabNM: tabNm,
        ep_click_sortType: sortType,
        ep_click_periodType: periodType,
        ep_click_discountRate: `${(
          product?.finalDiscountRate || 0
        ).toString()}%`,
        ep_click_myheartQTY: (product?.heartCnt || 0).toString(),
        ep_click_reviewQTY: (product?.reviewCnt || 0).toString(),
        ep_click_reviewScore: (
          product?.reviewScore.toFixed(1) || 0.0
        ).toString(),
        ep_order_ecommercePath: `select_item>${ecommercePath}`,
        ep_click_tagExclusive: getExclusiveTag(exclusiveYn, product),
        ep_click_referralID: referralID,
        ep_click_referralCat: referralCat,
        ep_click_referralNM: referralNM,
        ep_click_playYN: playYn,
        ep_click_variable: variable,
        ep_click_traceID: clickTraceId,
        ep_page_issueCD: pageIssueCd,
      };

      const productParam = {
        item_id: product?.itemCd,
        item_name: product?.itemName,
        item_brand: product?.brandNameEn,
        item_category: product?.mediumName,
        item_category2: product?.categoryDepthName1,
        item_category3: product?.categoryDepthName2,
        item_category4: product?.categoryDepthName3,
        item_category5: getItemCategory5(
          product?.itemNameFront || '',
          product?.itemNameBack || ''
        ),
        price: product?.finalPrice,
        currency: 'KRW',
        discount:
          product?.customerPrice && product?.finalPrice
            ? product.customerPrice - product.finalPrice
            : 0,
        index: listNo?.toString() || undefined,
        item_list_id: area,
        item_list_name: itemListName ?? page ?? pageInfo?.page ?? '',
        affiliation,
      };

      const actionList = {
        value: product?.finalPrice,
        currency: 'KRW',
      };

      const metric = {};
      sendGAVirEcommerce(
        appInfo?.isNativeAppBoth,
        !isMobile,
        eventName,
        productParam,
        actionList,
        data,
        metric
      );
      const logData = {
        E_step: eventName,
        ...data,
        ...productParam,
        ...actionList,
        ...metric,
      };
      requestLogDelivery(eventName, logData, assistData?.user?.userNo);

      // byteplus
      writeUserEvents({
        data: [
          {
            user_id: assistData.user.userNo.toString(),
            event_type: 'click',
            event_timestamp: Math.floor(Date.now() / 1000),
            page_number: 1,
            scene_name: pageInfo?.page,
            product_id: product?.itemCd,
            offset: Number(listNo || 0),
            platform: getByteplusPlatform(appInfo.isNativeAppBoth),
            item_type: 'product',
          },
        ],
      });
    },
    [getCommonParams, pageInfo, appInfo, isMobile, assistData, writeUserEvents]
  );

  const sendGa4AddToWishProduct = useCallback(
    ({
      product,
      listNo,
      pageNo,
      myheartYn,
      ecommercePath,
      area,
      tabNm,
      sortType,
      periodType,
      affiliation,
      exclusiveYn,
      page,
      referralID,
      referralCat,
      referralNM,
      variable,
      pageIssueCd,
      clickTraceId,
      text,
      itemListName,
    }: CommonGa4EventParams & {
      product?: Product;
      sortType?: string;
      periodType?: string;
      affiliation?: string;
      myheartYn?: boolean;
      ecommercePath?: string;
      pageNo?: number;
      exclusiveYn?: boolean;
      page?: string;
      referralID?: string;
      referralCat?: string;
      referralNM?: string;
      variable?: string;
      pageIssueCd?: string;
      clickTraceId?: string;
      text?: string;
      itemListName?: string;
    }) => {
      const commonParams = getCommonParams(page);
      const eventName = 'add_to_wishlist';
      const data = {
        event_name: eventName,
        ...commonParams,
        ep_click_area: area,
        ep_click_text: text,
        ep_click_listNo: listNo?.toString(),
        ep_click_pageNo: pageNo?.toString() || undefined,
        ep_click_tabNM: tabNm,
        ep_click_sortType: sortType,
        ep_click_periodType: periodType,
        ep_click_discountRate: `${(
          product?.finalDiscountRate || 0
        ).toString()}%`,
        ep_click_myheartQTY: (product?.heartCnt || 0).toString(),
        ep_click_reviewQTY: (product?.reviewCnt || 0).toString(),
        ep_click_reviewScore: (
          product?.reviewScore.toFixed(1) || 0.0
        ).toString(),
        ep_click_myheartYN: myheartYn ? 'Y' : 'N',
        ep_click_myheartType: '상품',
        ep_order_ecommercePath: `add_to_wishlist>${ecommercePath}`,
        ep_click_tagExclusive: getExclusiveTag(exclusiveYn, product),
        ep_click_referralID: referralID,
        ep_click_referralCat: referralCat,
        ep_click_referralNM: referralNM,
        ep_click_affiliation: affiliation,
        ep_click_variable: variable,
        ep_click_traceID: clickTraceId,
        ep_page_issueCD: pageIssueCd,
      };

      const productParam = {
        item_id: product?.itemCd,
        item_name: product?.itemName,
        item_brand: product?.brandNameEn,
        item_category: product?.mediumName,
        item_category2: product?.categoryDepthName1,
        item_category3: product?.categoryDepthName2,
        item_category4: product?.categoryDepthName3,
        item_category5: getItemCategory5(
          product?.itemNameFront || '',
          product?.itemNameBack || ''
        ),
        price: product?.finalPrice,
        currency: 'KRW',
        discount:
          product?.customerPrice && product?.finalPrice
            ? product.customerPrice - product.finalPrice
            : 0,
        index: listNo?.toString(),
        item_list_id: area,
        item_list_name: itemListName ?? page ?? pageInfo?.page ?? '',
        affiliation,
      };

      const actionList = {
        value: product?.finalPrice,
        currency: 'KRW',
      };

      const metric = {};
      sendGAVirEcommerce(
        appInfo?.isNativeAppBoth,
        !isMobile,
        eventName,
        productParam,
        actionList,
        data,
        metric
      );
      const logData = {
        E_step: eventName,
        ...data,
        ...productParam,
        ...actionList,
        ...metric,
      };
      requestLogDelivery(eventName, logData, assistData?.user?.userNo);
    },
    [getCommonParams, pageInfo, appInfo, isMobile, assistData]
  );

  const sendGa4AddToWishBrand = useCallback(
    ({
      id,
      name,
      ecommercePath,
      area,
      myheartYn,
      tabNm,
      listNo,
      pageNo,
      affiliation,
      exclusiveYn,
      text,
      pageIssueCd,
      clickTraceId,
      sortType,
    }: CommonGa4EventParams & {
      tabNm?: string;
      id?: string;
      name?: string;
      ecommercePath?: string;
      myheartYn?: boolean;
      listNo?: number;
      pageNo?: number;
      affiliation?: string;
      exclusiveYn?: string;
      text?: string;
      pageIssueCd?: string;
      clickTraceId?: string;
      sortType?: string;
    }) => {
      const commonParams = getCommonParams();
      const eventName = 'add_to_wishlist';
      const data = {
        event_name: eventName,
        ...commonParams,
        ep_click_area: area,
        ep_click_tabNM: tabNm,
        ep_click_myheartYN: myheartYn ? 'Y' : 'N',
        ep_click_myheartType: '브랜드',
        ep_click_listNo: listNo?.toString() || undefined,
        ep_click_pageNo: pageNo?.toString() || undefined,
        ep_click_affiliation: affiliation,
        ep_order_ecommercePath: `add_to_wishlist>${ecommercePath}`,
        ep_click_tagExclusive: exclusiveYn,
        item_id: id,
        item_name: name,
        ep_click_text: text,
        ep_click_traceID: clickTraceId,
        ep_page_issueCD: pageIssueCd,
        ep_click_sortType: sortType,
      };
      sendGAVirEcommerce(
        appInfo?.isNativeAppBoth,
        !isMobile,
        eventName,
        {},
        {},
        data,
        {}
      );
      const logData = {
        E_step: eventName,
        ...data,
      };
      requestLogDelivery(eventName, logData, assistData?.user?.userNo);
    },
    [appInfo?.isNativeAppBoth, getCommonParams, isMobile, assistData]
  );

  const sendGa4AddToWishContent = useCallback(
    ({
      variable,
      myheartYn,
      tabNm,
      area,
      issueCd,
      issueNm,
      ecommercePath,
      text,
      listNo,
      pageNo,
      profileSeqNo,
      pageIssueCd,
      clickTraceId,
    }: CommonGa4EventParams & {
      myheartYn?: boolean;
      tabNm?: string;
      issueCd?: string;
      issueNm?: string;
      ecommercePath?: string;
      bannerType?: string;
      text?: string;
      listNo?: number;
      pageNo?: number;
      profileSeqNo?: string;
      pageIssueCd?: string;
      clickTraceId?: string;
    }) => {
      const commonParams = getCommonParams();
      const eventName = 'add_to_wishlist';
      const data = {
        event_name: eventName,
        ...commonParams,
        ep_click_area: area,
        ep_click_text: text,
        ep_click_listNo: listNo?.toString() || undefined,
        ep_click_pageNo: pageNo?.toString() || undefined,
        ep_click_tabNM: tabNm,
        ep_click_myheartYN: myheartYn ? 'Y' : 'N',
        ep_click_myheartType: '콘텐츠',
        ep_click_variable: variable,
        ep_order_ecommercePath: ecommercePath
          ? `add_to_wishlist>${ecommercePath}`
          : undefined,
        ep_click_issueCD: issueCd,
        ep_click_issueNM: issueNm,
        ep_click_profileCD: profileSeqNo || undefined,
        ep_click_traceID: clickTraceId,
        ep_page_issueCD: pageIssueCd,
      };

      sendGAVirEcommerce(
        appInfo?.isNativeAppBoth,
        !isMobile,
        eventName,
        {},
        {},
        data,
        {}
      );
      const logData = {
        E_step: eventName,
        ...data,
      };
      requestLogDelivery(eventName, logData, assistData?.user?.userNo);
    },
    [appInfo?.isNativeAppBoth, getCommonParams, isMobile, assistData]
  );

  const sendGa4TabClick = useCallback(
    ({
      text,
      area,
      listNo,
      issueCd,
      issueNm,
      variable,
      tabNm,
      pageNo,
      page,
      pageIssueCd,
      clickTraceId,
    }: CommonGa4EventParams & {
      text?: string;
      listNo?: number;
      page?: string;
      pageIssueCd?: string;
      clickTraceId?: string;
    }) => {
      const commonParams = getCommonParams(page);
      const eventName = 'tab_click';
      const data = {
        event_name: eventName,
        ...commonParams,
        ep_click_issueCD: issueCd,
        ep_click_issueNM: issueNm,
        ep_click_area: area,
        ep_click_text: text,
        ep_click_listNo: listNo?.toString() || undefined,
        ep_click_variable: variable,
        ep_click_tabNM: tabNm,
        ep_click_pageNo: pageNo?.toString() || undefined,
        ep_click_traceID: clickTraceId,
        ep_page_issueCD: pageIssueCd,
      };
      sendGAClickEvent(appInfo?.isNativeAppBoth, !isMobile, data);
      const logData = {
        E_step: eventName,
        ...data,
      };
      requestLogDelivery(eventName, logData, assistData?.user?.userNo);
    },
    [appInfo?.isNativeAppBoth, getCommonParams, isMobile, assistData]
  );

  const sendGa4FilterClick = useCallback(
    ({
      text,
      area,
      selectedFilters,
      listNo,
      tabNm,
      page,
    }: CommonGa4EventParams & {
      text?: string;
      selectedFilters?: FilterItem[];
      tabNm?: string;
    }) => {
      const commonParams = getCommonParams();
      const flterParams = getFilterParams(selectedFilters);
      const eventName = 'filter_click';
      const data = {
        event_name: eventName,
        ...commonParams,
        ep_click_area: area,
        ep_click_page: page,
        ep_click_text: text || undefined,
        ep_click_listNo: listNo?.toString() || undefined,
        ep_click_tabNM: tabNm,
        ...flterParams,
      };
      sendGAClickEvent(appInfo?.isNativeAppBoth, !isMobile, data);
      const logData = {
        E_step: eventName,
        ...data,
      };
      requestLogDelivery(eventName, logData, assistData?.user?.userNo);
    },
    [
      appInfo?.isNativeAppBoth,
      assistData,
      getCommonParams,
      getFilterParams,
      isMobile,
    ]
  );

  const sendGa4BannerClick = useCallback(
    ({
      text,
      area,
      bannerType,
      listNo,
      issueNm,
      issueCd,
      tabNm,
      pageNo,
      bannerForm,
      page,
      variable,
      profileSeqNo,
      pageIssueCd,
      adYn,
      bannerCd,
      clickTraceId,
    }: CommonGa4EventParams & {
      bannerType?: string;
      listNo?: number;
      issueNm?: string;
      issueCd?: string;
      tabNm?: string;
      pageNo?: number;
      bannerForm?: string;
      page?: string;
      profileCd?: string;
      profileSeqNo?: string;
      pageIssueCd?: string;
      adYn?: string;
      bannerCd?: string;
      clickTraceId?: string;
    }) => {
      const commonParams = getCommonParams();
      const eventName = 'banner_click';
      const data = {
        event_name: eventName,
        ...commonParams,
        ep_click_area: area,
        ep_click_page: page || pageInfo?.page,
        ep_click_text: text,
        ep_click_listNo: listNo?.toString() || undefined,
        ep_click_bannerType: bannerType,
        ep_click_issueCD: issueCd,
        ep_click_issueNM: issueNm,
        ep_click_tabNM: tabNm,
        ep_click_pageNo: pageNo?.toString() || undefined,
        ep_click_bannerForm: bannerForm || undefined,
        ep_click_variable: variable,
        ep_click_profileCD: profileSeqNo || undefined,
        ep_click_traceID: clickTraceId,
        ep_page_issueCD: pageIssueCd,
        ep_click_adYN: adYn || undefined,
        ep_click_bannerCD: bannerCd || undefined,
      };
      sendGAClickEvent(appInfo?.isNativeAppBoth, !isMobile, data);
      const logData = {
        E_step: eventName,
        ...data,
      };
      requestLogDelivery(eventName, logData, assistData?.user?.userNo);
    },
    [getCommonParams, pageInfo, appInfo, isMobile, assistData]
  );

  const sendGa4BrandClick = useCallback(
    ({
      text,
      area,
      listNo,
      tabNm,
      exclusiveYn,
      sortType,
      issueCd,
      issueNm,
      page,
    }: CommonGa4EventParams & {
      listNo?: number;
      tabNm?: string;
      exclusiveYn?: string;
      sortType?: string;
      issueCd?: string;
      issueNm?: string;
    }) => {
      const commonParams = getCommonParams(page);
      const eventName = 'brand_click';
      const data = {
        event_name: eventName,
        ...commonParams,
        ep_click_area: area,
        ep_click_text: text,
        ep_click_listNo: listNo?.toString() || undefined,
        ep_click_tagExclusive: exclusiveYn,
        ep_click_tabNM: tabNm,
        ep_click_sortType: sortType,
        ep_click_issueCD: issueCd,
        ep_click_issueNM: issueNm,
      };

      sendGAClickEvent(appInfo?.isNativeAppBoth, !isMobile, data);
      const logData = {
        E_step: eventName,
        ...data,
      };
      requestLogDelivery(eventName, logData, assistData?.user?.userNo);
    },
    [appInfo?.isNativeAppBoth, getCommonParams, isMobile, assistData]
  );

  const sendGa4ButtonClick = useCallback(
    ({
      text,
      area,
      issueCd,
      issueNm,
      tabNm,
      bannerType,
      listNo,
      pageNo,
      variable,
      profileNo,
      bannerForm,
      searchType,
      pageIssueCd,
      clickTraceId,
      page,
    }: CommonGa4EventParams & {
      text?: string;
      issueCd?: string;
      issueNm?: string;
      tabNm?: string;
      bannerType?: string;
      listNo?: number;
      pageNo?: number;
      variable?: string;
      profileNo?: string;
      bannerForm?: string;
      searchType?: string;
      page?: string;
      pageIssueCd?: string;
      clickTraceId?: string;
    }) => {
      const commonParams = getCommonParams(page);
      const eventName = 'button_click';
      const data = {
        event_name: eventName,
        ...commonParams,
        ep_click_area: area,
        ep_click_text: text,
        ep_click_listNo: listNo?.toString() || undefined,
        ep_click_issueCD: issueCd,
        ep_click_issueNM: issueNm,
        ep_click_tabNM: tabNm || undefined,
        ep_click_bannerType: bannerType,
        ep_click_pageNo: pageNo?.toString() || undefined,
        ep_click_variable: variable,
        ep_click_profileCD: profileNo || undefined,
        ep_click_bannerForm: bannerForm || undefined,
        ep_click_searchType: searchType || undefined,
        ep_click_traceID: clickTraceId,
        ep_page_issueCD: pageIssueCd,
      };
      sendGAClickEvent(appInfo?.isNativeAppBoth, !isMobile, data);
      const logData = {
        E_step: eventName,
        ...data,
      };
      requestLogDelivery(eventName, logData, assistData?.user?.userNo);
    },
    [appInfo?.isNativeAppBoth, getCommonParams, isMobile, assistData]
  );

  const sendGa4CouponClick = useCallback(
    ({
      text,
      area,
      issueCd,
      issueNm,
      tabNm,
      bannerType,
      listNo,
      pageNo,
      couponCd,
      couponNm,
      variable,
      page,
    }: CommonGa4EventParams & {
      text?: string;
      issueCd?: string;
      issueNm?: string;
      tabNm?: string;
      bannerType?: string;
      listNo?: number;
      pageNo?: number;
      couponCd?: string;
      couponNm?: string;
      variable?: string;
    }) => {
      const commonParams = getCommonParams(page);
      const eventName = 'coupon_click';
      const data = {
        event_name: eventName,
        ...commonParams,
        ep_click_area: area,
        ep_click_text: text,
        ep_click_listNo: listNo?.toString() || undefined,
        ep_click_issueCD: issueCd,
        ep_click_issueNM: issueNm,
        ep_click_tabNM: tabNm || undefined,
        ep_click_bannerType: bannerType,
        ep_click_pageNo: pageNo?.toString() || undefined,
        ep_click_couponCD: couponCd,
        ep_click_couponNM: couponNm,
        ep_click_variable: variable,
      };
      console.log('data', data);
      sendGAClickEvent(appInfo?.isNativeAppBoth, !isMobile, data);
      const logData = {
        E_step: eventName,
        ...data,
      };
      requestLogDelivery(eventName, logData, assistData?.user?.userNo);
    },
    [appInfo?.isNativeAppBoth, getCommonParams, isMobile, assistData]
  );

  const sendGa4PageView = useCallback(
    async ({ title }: { title: string }) => {
      const pageObj = {
        title,
        location: window.location.href,
        ep_visit_domainGender: domainGender,
        ep_visit_device: getEpDevice(),
      };
      const pageviewObj = await getGa4UserProperty(pageObj);

      gaVirtualPageView(!!appInfo?.isNativeAppBoth, pageviewObj);
    },
    [appInfo?.isNativeAppBoth, domainGender, getEpDevice]
  );

  const sendGa4ContentView = useCallback(
    ({
      contentsCat,
      contentsDetail,
      style,
      tpo,
      trend,
      approvalDate,
      issueCd,
      issueNm,
      title,
      profileNo,
      profileType,
      creatorYN,
    }: CommonGa4EventParams & {
      contentsCat?: string;
      contentsDetail?: string;
      style?: string;
      tpo?: string;
      trend?: string;
      approvalDate?: string;
      issueCd?: string;
      issueNm?: string;
      title: string;
      profileNo?: string;
      profileType?: string;
      creatorYN?: string;
    }) => {
      const commonParams = getCommonParams();
      const eventName = 'contents_view';
      const data = {
        event_name: eventName,
        page_title: title,
        page_location: window.location.href,
        ...commonParams,
        ep_page_contentsCat: contentsCat,
        ep_page_contentsDetail: contentsDetail,
        ep_page_style: style || '해당값 없음',
        ep_page_tpo: tpo || '해당값 없음',
        ep_page_trend: trend || '해당값 없음',
        ep_page_approvalDate: approvalDate,
        ep_visit_device: getEpDevice(),
        ep_page_issueCD: issueCd,
        ep_page_issueNM: issueNm,
        ep_page_profileCD: profileNo || undefined,
        ep_page_profileType: profileType || undefined,
        ep_page_creatorYN: creatorYN || undefined,
      };
      sendGAClickEvent(appInfo?.isNativeAppBoth, !isMobile, data);
      const logData = {
        E_step: eventName,
        ...data,
      };
      requestLogDelivery(eventName, logData, assistData?.user?.userNo);
    },
    [getCommonParams, getEpDevice, appInfo, isMobile, assistData]
  );

  const sendGa4BannerView = useCallback(
    ({
      issueCd,
      adYn,
      bannerCd,
      text,
      type,
      listNo,
    }: CommonGa4EventParams & {
      issueCd?: string;
      adYn?: string;
      bannerCd?: string;
      text?: string;
      type?: string;
      listNo?: number;
    }) => {
      const commonParams = getCommonParams();
      const eventName = 'banner_view';
      const data = {
        event_name: eventName,
        ep_visit_device: getEpDevice(),
        ep_page_issueCD: issueCd || undefined,
        ep_banner_adYN: adYn || undefined,
        ep_banner_bannerCD: bannerCd || undefined,
        ep_banner_text: text,
        ep_banner_type: type,
        ep_banner_listNo: listNo?.toString() || undefined,
      };
      sendGAClickEvent(appInfo?.isNativeAppBoth, !isMobile, data);
      const logData = {
        E_step: eventName,
        ...data,
      };
      requestLogDelivery(eventName, logData, assistData?.user?.userNo);
    },
    [getCommonParams, getEpDevice, appInfo, isMobile, assistData]
  );

  const sendGa4SubGnbView = useCallback(
    ({ issueCd, issueNm }: { issueCd?: string; issueNm?: string }) => {
      const eventName = 'subgnb_view';
      const data = {
        event_name: eventName,
        ep_visit_device: getEpDevice(),
        ep_page_issueCD: issueCd || undefined,
        ep_page_issueNM: issueNm || undefined,
      };
      sendGAClickEvent(appInfo?.isNativeAppBoth, !isMobile, data);
      const logData = {
        E_step: eventName,
        ...data,
      };
      requestLogDelivery(eventName, logData, assistData?.user?.userNo);
    },
    [getEpDevice, appInfo, isMobile, assistData]
  );

  const sendGa4ScrollClick = useCallback(
    ({
      title,
      area,
      contentsCat,
      contentsDetail,
      style,
      tpo,
      trend,
      approvalDate,
      issueCd,
      issueNm,
      contentsPlaytime,
      contentsTime,
      pageNo,
    }: // viewComplete,
    CommonGa4EventParams & {
      area?: string;
      contentsCat?: string;
      contentsDetail?: string;
      style?: string;
      tpo?: string;
      trend?: string;
      approvalDate?: string;
      issueCd?: string;
      issueNm?: string;
      contentsPlaytime?: number;
      contentsTime?: number;
      pageNo?: number;
      // viewComplete?: number;
      title: string;
    }) => {
      const commonParams = getCommonParams();
      const eventName = 'scroll_click';
      const data = {
        event_name: eventName,
        page_title: title,
        page_location: window.location.href,
        ...commonParams,
        ep_click_area: area,
        ep_scroll_contentsCat: contentsCat,
        ep_scroll_contentsDetail: contentsDetail,
        ep_scroll_contentsPlaytime: contentsPlaytime,
        ep_scroll_contentsTime: contentsTime,
        ep_scroll_style: style || '해당값 없음',
        ep_scroll_tpo: tpo || '해당값 없음',
        ep_scroll_trend: trend || '해당값 없음',
        ep_scroll_approvalDate: approvalDate,
        ep_visit_device: getEpDevice(),
        ep_scroll_issueCD: issueCd,
        ep_scroll_issueNM: issueNm,
        ep_scroll_pageNo: pageNo,
        // ep_scroll_viewComplete: viewComplete,
      };

      sendGAClickEvent(appInfo?.isNativeAppBoth, !isMobile, data);
      const logData = {
        E_step: eventName,
        ...data,
      };
      requestLogDelivery(eventName, logData, assistData?.user?.userNo);
    },
    [getCommonParams, getEpDevice, appInfo, isMobile, assistData]
  );

  const sendGa4HeaderClick = useCallback(
    ({
      text,
      page,
    }: CommonGa4EventParams & { text?: string; page?: string }) => {
      const commonParams = getCommonParams();
      const eventName = 'header_click';
      const data = {
        event_name: eventName,
        ...commonParams,
        ep_click_page: page || pageInfo?.page,
        ep_click_text: text,
      };
      sendGAClickEvent(appInfo?.isNativeAppBoth, !isMobile, data);
      const logData = {
        E_step: eventName,
        ...data,
      };
      requestLogDelivery(eventName, logData, assistData?.user?.userNo);
    },
    [appInfo?.isNativeAppBoth, getCommonParams, isMobile, assistData, pageInfo]
  );

  const sendGa4ActionBarClick = useCallback(
    ({
      text,
      page,
    }: CommonGa4EventParams & { text?: string; page?: string }) => {
      const commonParams = getCommonParams();
      const eventName = 'actionbar_click';
      const data = {
        event_name: eventName,
        ...commonParams,
        ep_click_page: page,
        ep_click_text: text,
      };
      sendGAClickEvent(appInfo?.isNativeAppBoth, !isMobile, data);
      const logData = {
        E_step: eventName,
        ...data,
      };
      requestLogDelivery(eventName, logData, assistData?.user?.userNo);
    },
    [appInfo?.isNativeAppBoth, getCommonParams, isMobile, assistData]
  );

  const sendGa4FooterClick = useCallback(
    ({
      text,
      page,
    }: CommonGa4EventParams & { text?: string; page?: string }) => {
      const commonParams = getCommonParams();
      const eventName = 'footer_click';
      const data = {
        event_name: eventName,
        ...commonParams,
        ep_click_page: page || pageInfo?.page,
        ep_click_text: text,
      };
      sendGAClickEvent(appInfo?.isNativeAppBoth, !isMobile, data);
      const logData = {
        E_step: eventName,
        ...data,
      };
      requestLogDelivery(eventName, logData, assistData?.user?.userNo);
    },
    [appInfo?.isNativeAppBoth, getCommonParams, isMobile, assistData, pageInfo]
  );

  const sendGa4GnbClick = useCallback(
    ({
      text,
      area,
      page,
      listNo,
      adYn,
    }: CommonGa4EventParams & {
      text?: string;
      area?: string;
      page?: string;
      listNo?: string;
      adYn?: string;
    }) => {
      const commonParams = getCommonParams();
      const eventName = 'gnb_click';
      const data = {
        event_name: eventName,
        ...commonParams,
        ep_click_page: page || pageInfo?.page,
        ep_click_area: area,
        ep_click_text: text,
        ep_click_listNo: listNo,
        ep_click_adYN: adYn || undefined,
      };
      sendGAClickEvent(appInfo?.isNativeAppBoth, !isMobile, data);
      const logData = {
        E_step: eventName,
        ...data,
      };
      requestLogDelivery(eventName, logData, assistData?.user?.userNo);
    },
    [appInfo?.isNativeAppBoth, getCommonParams, isMobile, assistData, pageInfo]
  );

  const sendGa4LnbClick = useCallback(
    ({
      area,
      page,
      text,
      listNo,
      tabNm,
      variable,
      sortType,
      issueCd,
      issueNm,
      bannerType,
    }: CommonGa4EventParams & {
      area?: string;
      page?: string;
      text?: string;
      listNo?: string;
      tabNm?: string;
      variable?: string;
      sortType?: string;
      issueCd?: string;
      issueNm?: string;
      bannerType?: string;
    }) => {
      const commonParams = getCommonParams();
      const eventName = 'lnb_click';
      const data = {
        event_name: eventName,
        ...commonParams,
        ep_click_page: page || pageInfo?.page,
        ep_click_area: area,
        ep_click_text: text,
        ep_click_listNo: listNo,
        ep_click_tabNM: tabNm,
        ep_click_variable: variable,
        ep_click_sortType: sortType,
        ep_click_issueCD: issueCd,
        ep_click_issueNM: issueNm,
        ep_click_bannerType: bannerType,
      };
      sendGAClickEvent(appInfo?.isNativeAppBoth, !isMobile, data);
      const logData = {
        E_step: eventName,
        ...data,
      };
      requestLogDelivery(eventName, logData, assistData?.user?.userNo);
    },
    [appInfo?.isNativeAppBoth, getCommonParams, isMobile, assistData, pageInfo]
  );

  const sendGa4KeywordClick = useCallback(
    ({
      text,
      area,
      tabNm,
      page,
      pageNo,
      searchType,
    }: {
      text: string;
      area: string;
      tabNm?: string;
      page?: string;
      pageNo?: number;
      searchType?: string;
    }) => {
      const commonParams = getCommonParams();
      const eventName = 'keyword_click';
      const data = {
        event_name: eventName,
        ...commonParams,
        ep_click_pageNo: pageNo?.toString() || undefined,
        ep_click_area: area,
        ep_click_text: text,
        ep_click_tabNM: tabNm,
        ep_click_searchType: searchType,
        ep_click_page: page || pageInfo?.page,
      };
      sendGAClickEvent(appInfo?.isNativeAppBoth, !isMobile, data);
      const logData = {
        E_step: eventName,
        ...data,
      };
      requestLogDelivery(eventName, logData, assistData?.user?.userNo);
    },
    [appInfo?.isNativeAppBoth, getCommonParams, isMobile, assistData, pageInfo]
  );

  return {
    sendGa4SelectItem,
    sendGa4AddToWishProduct,
    sendGa4AddToWishBrand,
    sendGa4AddToWishContent,
    sendGa4TabClick,
    sendGa4FilterClick,
    sendGa4BannerClick,
    sendGa4BrandClick,
    sendGa4ButtonClick,
    sendGa4ContentView,
    sendGa4BannerView,
    sendGa4SubGnbView,
    sendGa4HeaderClick,
    sendGa4ActionBarClick,
    sendGa4GnbClick,
    sendGa4FooterClick,
    sendGa4LnbClick,
    sendGa4KeywordClick,
    getSortText,
    getEpDevice,
    getIssueCd,
    getContentTypeText,
    getContentReferralId,
    sendGa4ScrollClick,
    sendGa4PageView,
    getContentName,
    getAdContentReferralId,
    getAdContentName,
    sendGa4CouponClick,
    getContentFormType,
    getCornerAreaName,
    getShortFormTabName,
  };
}

export default useGa4Event;
